import {useEffect, useRef, useState, type SyntheticEvent} from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import {IconOnlyButton} from '../button/icon-only-button/icon-only-button';
import type {ButtonVariant} from '../button/icon-only-button/icon-only-button';
import {KebabRegularIcon} from '../icon/svg/kebab-regular-icon';
import type {LocationEnum} from '../popover/popover';
import {SelectMenu} from '../select-menu/select-menu';
import type {SelectMenuOption} from '../select-menu/select-menu';

export type {SelectMenuOption};

interface Props {
    options: SelectMenuOption[];
    tooltipText?: string;
    size?: 'normal' | 'large';
    variant?: ButtonVariant;
    location?: LocationEnum;
    offsetX?: number;
    offsetY?: number;
    onOpenChange?: (isOpen: boolean) => void;
    isDisabled?: boolean;
    getTriggerRef?: (node: HTMLElement | null) => void;
}

export function IconActionMenu(props: Props) {
    const {
        options,
        tooltipText,
        size = 'normal',
        variant = 'default',
        location = 'bottom-left',
        offsetX,
        offsetY,
        onOpenChange,
        isDisabled,
        getTriggerRef,
    } = props;

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [postCloseTooltipText, setPostCloseTooltipText] = useState<string | null>(null);
    const anchorRef = useRef<HTMLElement | null>(null);

    const displayTooltip = postCloseTooltipText ?? tooltipText;

    const setOpen = (next: boolean) => {
        setIsOpen(next);
        if (onOpenChange) {
            onOpenChange(next);
        }
    };

    useEffect(() => {
        if (!postCloseTooltipText) {
            return;
        }
        const node = anchorRef.current;
        if (!node) {
            return;
        }
        ReactTooltip.show(node);
        const timeoutId = window.setTimeout(() => {
            ReactTooltip.hide(node);
            setPostCloseTooltipText(null);
        }, 2000);
        return () => {
            window.clearTimeout(timeoutId);
            ReactTooltip.hide(node);
        };
    }, [postCloseTooltipText]);

    const handleTriggerClick = (e: SyntheticEvent<any>) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(!isOpen);
    };

    const handleClose = (e?: SyntheticEvent | Event, closeTooltipText?: string | null) => {
        if (e && 'isPropagationStopped' in e && !e.isPropagationStopped()) {
            e.stopPropagation();
        }
        setOpen(false);
        if (closeTooltipText) {
            setPostCloseTooltipText(closeTooltipText);
        }
    };

    return (
        <>
            <IconOnlyButton
                icon={KebabRegularIcon}
                onClick={handleTriggerClick}
                tooltipText={displayTooltip}
                size={size}
                variant={variant}
                forceHover={isOpen}
                isDisabled={isDisabled}
                getButtonRef={(node: HTMLElement | null) => {
                    anchorRef.current = node;
                    if (getTriggerRef) {
                        getTriggerRef(node);
                    }
                }}
            />
            {isOpen && (
                <SelectMenu
                    anchor={anchorRef.current}
                    options={options}
                    onClose={handleClose}
                    location={location}
                    offsetX={offsetX}
                    offsetY={offsetY ?? 4}
                />
            )}
        </>
    );
}
